import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Section } from '@expo/html-elements';
import {
  Container,
  HStack,
  theme,
  useBreakpointHelpers,
  useResponsive,
} from '@sweetgreen/garnish';

import { LoadingAnimation, PageWrapper } from '@order/components';
import { useDeliveryOrderInFlight } from '@order/hooks';

import { BagAccessButton } from '../../components/Bag/subcomponents';
import { FeaturedChannels } from '../../components/FeaturedChannels';
import type { HomeScreenProps } from '../../navigation/AppNavigation.props';
import { MobileHeader } from './components/MobileHeader';
import { WelcomePanePure } from './components/WelcomePane';
import { useHomeData } from './HomeScreen.data';
import { useHomeScreenHeroCTAHandler } from './HomeScreen.heroCTA';
import { useHomeScreenLoadedContent } from './HomeScreen.loaded';
import {
  useFeaturedChannelNavigation,
  useHomeNavigation,
} from './HomeScreen.navigation';
import { HomeScreenHero, ReorderRail } from './subcomponents';

export const HomeScreen = ({
  navigation,
}: HomeScreenProps): React.ReactElement => {
  const { isMobile } = useBreakpointHelpers();

  const loadedContent = useHomeScreenLoadedContent();

  const data = useHomeData();

  useDeliveryOrderInFlight(); // results later used from cache.

  React.useLayoutEffect(() => {
    navigation.setOptions({ headerShown: !isMobile });
  }, [navigation, isMobile]);

  const handlePressScanToPay = React.useCallback(() => {
    navigation.navigate('Modal', { screen: 'ScanToPay' });
  }, [navigation]);

  const handlePressHeroCTA = useHomeScreenHeroCTAHandler(
    loadedContent.heroCTA?.action,
  );

  if (data.isLoading) return <LoadingAnimation />;

  return (
    <HomeScreenContent
      loadedContent={loadedContent}
      onPressHeroCTA={handlePressHeroCTA}
      onPressScanToPay={handlePressScanToPay}
      {...data}
    />
  );
};

type LoadedContent = ReturnType<typeof useHomeScreenLoadedContent>;

type HomeScreenContentProps = {
  readonly loadedContent: LoadedContent;
  readonly onPressHeroCTA: () => void;
  readonly onPressScanToPay: () => void;
} & ReturnType<typeof useHomeData>;

export const HomeScreenContent = React.memo(
  (props: HomeScreenContentProps): React.ReactElement => {
    const {
      orders,
      status,
      deliveryRestaurant,
      deliveryVendor,
      cartOrderType,
      isCartEmpty,
      isLoggedIn,
      isLoading,
      isLoadingOrders,
      hasErrors,
      loadedContent,
      orderPendingFeedbackId,
      onPressHeroCTA,
      onPressScanToPay,
    } = props;
    const { currentBreakpoint, match } = useResponsive();
    const gridColumnsNumber = currentBreakpoint.isMobile ? 1 : 2;
    const gridGap = match([0, theme.spacing.large, theme.spacing.xlarge]);
    const { handleGoToDefaultLocations } = useHomeNavigation();
    const onFeaturedChannelSelection = useFeaturedChannelNavigation();

    // ─── STYLES ─────────────────────────────────────────────────────────

    const containerStyles = [
      styles.containerWithGutters,
      match([styles.containerXS, styles.containerSM]),
    ];

    // ────────────────────────────────────────────────────────────────────

    return (
      <>
        <PageWrapper
          testID="home.container"
          style={styles.wrapper}
          contentContainerStyle={styles.scrollContainer}
        >
          <SafeAreaView edges={['top']}>
            <View style={containerStyles}>
              <Container>
                {currentBreakpoint.isMobile ? (
                  <MobileHeader
                    onPressScanToPay={onPressScanToPay}
                    isLoggedIn={isLoggedIn}
                  />
                ) : null}

                <HStack itemsPerRow={gridColumnsNumber} customSpacing={gridGap}>
                  <Section style={styles.column}>
                    {currentBreakpoint.isMobile ? (
                      <WelcomePanePure
                        isLoading={isLoading}
                        isLoggedIn={isLoggedIn}
                        hasCart={!isCartEmpty}
                        cartOrderType={cartOrderType}
                        data={status}
                        orderPendingFeedbackId={orderPendingFeedbackId}
                        seasonalHeroHeadlineText={
                          loadedContent.heroHeadlineText
                        }
                      />
                    ) : null}

                    <HomeScreenHero
                      isLoading={isLoading}
                      ctaText={loadedContent.heroCTA?.text}
                      imageURL={loadedContent.heroImageURL}
                      onPressCTA={onPressHeroCTA}
                    />
                  </Section>

                  <Section style={styles.column}>
                    {!currentBreakpoint.isMobile && (
                      <WelcomePanePure
                        isLoading={isLoading}
                        isLoggedIn={isLoggedIn}
                        hasCart={!isCartEmpty}
                        cartOrderType={cartOrderType}
                        data={status}
                        orderPendingFeedbackId={orderPendingFeedbackId}
                        seasonalHeroHeadlineText={
                          loadedContent.heroHeadlineText
                        }
                      />
                    )}
                    <FeaturedChannels
                      isLoadingOrders={isLoadingOrders}
                      deliveryRestaurant={deliveryRestaurant}
                      deliveryVendor={deliveryVendor}
                      onFeaturedChannelSelection={onFeaturedChannelSelection}
                      onNavigateToLocations={handleGoToDefaultLocations}
                    />
                  </Section>
                </HStack>
              </Container>
            </View>

            <View>
              <ReorderRail
                isLoading={isLoadingOrders}
                orders={orders}
                hasError={hasErrors}
              />
            </View>
          </SafeAreaView>
        </PageWrapper>
        {currentBreakpoint.isMobile ? <BagAccessButton /> : null}
      </>
    );
  },
);

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
  },
  wrapper: {
    backgroundColor: theme.colors.KHAKI_3,
  },
  containerWithGutters: {
    flexGrow: 1,
    marginBottom: 'auto',
  },
  containerXS: {
    paddingTop: theme.spacing.medium,
    paddingBottom: theme.spacing.large,
  },
  containerSM: {
    paddingVertical: theme.spacing.xlarge,
  },
  column: {
    flexGrow: 1,
  },
});
